import React, { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faBirthdayCake } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid";

import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { TnABillableTimeOverviewDTO } from "../../../models/tna-billable-time-overviews/tna-billable-time-overview-dto";
import { TnAOverviewsFilter } from "../../../models/tna-overviews/tna-overview.filter";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import Table from "../../../reusable-components/Table/Table";
import { TnABillableTimeOverviewsApiService } from "../../../services/tna-billable-time-overviews/tna-billable-time-overviews-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_MTPAL } from "../../../utils/constants/filter-tables-constants";
import { getDayAndMonth, getDaysInMonth, intlFormatDateShort } from "../../../utils/time-utils";
import { openTimesheetViewMode } from "../../../views/my-t-and-a/my-ee-tna-utils";
import QuickfiltersContainer from "../../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { MyTnAContext } from "../MyTeamPresenceAndLeaves";
import { EmployeeNameContainer, MainTableContainer } from "./styles";
import { EmployeeDTO } from "../../../models/employees";
import { TnADayDataDTO } from "../../../models/tna/tna-data/tna-day-data-dto";
import { TnABillableTimeRef } from "../../../models/tna";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";
import { RefModel } from "../../../models/base/ref-model";
import ExportBillableTimeToExcel from "./ExportBillableTimeToExcel/ExportBillableTimeToExcel";

interface State {
  loading: boolean;
  error: string;
}

const TnABillableTimeOverviews = () => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_billable_time";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const theme = useTheme();

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
    searchingDate: { year, month, yearMonthQuickFilter },
  } = useContext(MyTnAContext);

  const navigate = useNavigate();

  const allItems = useRef<TnABillableTimeOverviewDTO[]>([]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
  });

  const clickNavigate = (employee: EmployeeDTO) => {
    if (!hasAccess && allowedToViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (hasAccess) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
    }
  };

  const columns: GridColumns = [
    {
      field: "employee.employeeCode, employee.internalId",
      headerName: `${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    clickNavigate(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      headerName: `${t(`common_words.employee`)} | ${t(`common_words.legal_entity`)}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "employee.tnAGroup",
      headerName: t(`common_words.tna_employee_group`),
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "billableTimeYTD",
      valueGetter: ({ row }) => `${!!row.billableTimeYTD ? row.billableTimeYTD.toFixed(2) : ""}`,
      headerName: t(`${translationFields}.ytd`),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "balance",
      renderCell: ({ row }) => {
        const balance = row.daysData
          ?.map((day: TnADayDataDTO) => day?.billableTime?.reduce((acc, curr) => acc + curr?.amount, 0))
          ?.reduce((acc: number, curr: number) => acc + curr, 0);

        return `${!!balance ? balance.toFixed(2) : ""}`;
      },
      headerName: t(`${translationFields}.balance`),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const dynamicColumns = (year: number, month: number) => {
    let result: GridColumns = [];
    const daysInMonth = getDaysInMonth(year, month);
    for (let i = 0; i < daysInMonth; i++) {
      result.push({
        field: new Date(year, month, i + 1).toString(),
        renderCell: ({ row }) => {
          const currDay = row?.daysData?.find((day: TnADayDataDTO) => day?.tnADate?.day === i + 1);
          const billableTimes = currDay?.billableTime?.reduce(
            (acc: number, curr: TnABillableTimeRef) => acc + curr?.amount,
            0
          );

          const isWeekend = [0, 6].indexOf(new Date(year, month, i + 1).getDay()) != -1;

          return !isWeekend && !!billableTimes && <span>{billableTimes.toFixed(2)}</span>;
        },
        headerName: getDayAndMonth(new Date(year, month, i + 1)),
        headerAlign: "center",
        align: "center",
        flex: 1,
        minWidth: 80,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: (params: any) => {
          const currDay = new Date(params.field);
          const dayOfWeek: number = currDay.getDay();
          const thisDayData = params.row?.daysData?.find(
            (day: TnADayDataDTO) => day?.tnADate?.day === currDay.getDate()
          );

          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          return thisDayData?.isPublicHoliday ? "isPublicHoliday" : isWeekend ? "isWeekend" : "";
        },
      });
    }

    return result;
  };

  const defaultItem: Partial<TnABillableTimeOverviewDTO> = {
    employee: null,
    daysData: [],
  };

  const loadEmployeeGroup = useCallback(async () => {
    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    }
  }, [employeeGroupOption?.employeeGroupIds]);

  const loadTnABillableTimeData = useCallback(
    async (pageSize: number = 10) => {
      try {
        const response = await new TnABillableTimeOverviewsApiService().loadItems(
          {
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
            onlyCurrentEmployees: !!onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
            year: year,
            month: month + 1,
          } as TnAOverviewsFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize: pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      legalEntityOption?.legalEntityIds,
      year,
      month,
    ]
  );

  const quickfilters = useCallback(
    (items: RefModel[], loading: boolean) => {
      return (
        <QuickfiltersContainer>
          <>
            {!!legalEntityQuickFilter && legalEntityQuickFilter}
            {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
            {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
            {!!yearMonthQuickFilter && yearMonthQuickFilter}
            <ExportBillableTimeToExcel
              loadEmployeeGroup={loadEmployeeGroup}
              loadTnABillableTimeData={loadTnABillableTimeData}
              disabled={state.loading || loading || !items?.length}
              allItems={() => allItems.current}
            />
          </>
        </QuickfiltersContainer>
      );
    },
    [state.loading, legalEntityQuickFilter, employeeGroupQuickFilter, onlyCurrentEmployeesQuickFilter]
  );

  return (
    <LoadingOverlay
      loading={state.loading}
      error={state.error}
    >
      <MainTableContainer>
        {(hasAccess || allowedToViewOnly) && (
          <Table
            translationRootPath={tRP}
            columns={columns}
            serviceApi={new TnABillableTimeOverviewsApiService()}
            propsFilter={
              {
                ...legalEntityOption,
                ...employeeGroupOption,
                ...onlyCurrentEmployeesOptionValue,
                year,
                month: month + 1,
              } as TnAOverviewsFilter
            }
            defaultItem={defaultItem}
            filter={FT_MTPAL}
            readOnly
            quickFilters={quickfilters}
            dynamicColumns={dynamicColumns(year, month)}
            getAllItems={(items) => {
              allItems.current = items as TnABillableTimeOverviewDTO[];
            }}
          />
        )}
      </MainTableContainer>
    </LoadingOverlay>
  );
};

export default TnABillableTimeOverviews;
